import { Logtail } from '@logtail/browser';
import type { User } from '@supabase/supabase-js';
import { CLIENT_VERSION_INFO } from './version-client';

type FrontendLogLevel = 'debug' | 'info' | 'warn' | 'error';

type LogtailUserPayload = {
  id: string
  email?: string
  username?: string
  provider?: string
};

type ResolveLogtailSourceTokenOptions = {
  token?: string | null
};

const PLACEHOLDER_TOKEN_PATTERNS = [
  /^your-(dev-)?better-stack-source-token$/i,
  /^your-(dev-)?logtail-source-token$/i,
  /^your-logtail-token$/i,
];

let browserClient: Logtail | null = null;
let browserClientInitialized = false;
let currentUser: LogtailUserPayload | null = null;

const trimString = (value: unknown) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const resolveLogtailEndpoint = (value?: string | null) => {
  const trimmed = trimString(value);
  if (!trimmed) {
    return undefined;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, '');
  }

  return `https://${trimmed.replace(/\/+$/, '')}`;
};

export const resolveLogtailSourceToken = ({
  token,
}: ResolveLogtailSourceTokenOptions): string | undefined => {
  const trimmedToken = trimString(token);
  if (!trimmedToken) {
    return undefined;
  }

  if (PLACEHOLDER_TOKEN_PATTERNS.some((pattern) => pattern.test(trimmedToken))) {
    return undefined;
  }

  return trimmedToken;
};

const getLogtailClient = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (browserClientInitialized) {
    return browserClient;
  }

  browserClientInitialized = true;

  const sourceToken = resolveLogtailSourceToken({
    token: process.env.NEXT_PUBLIC_BETTER_STACK_SOURCE_TOKEN,
  });

  const endpoint = resolveLogtailEndpoint(process.env.NEXT_PUBLIC_BETTER_STACK_INGESTING_HOST);

  if (!sourceToken || !endpoint) {
    return null;
  }

  browserClient = new Logtail(sourceToken, { endpoint });
  return browserClient;
};

const resolveUsername = (user: User) => {
  return (
    trimString(user.user_metadata?.full_name) ||
    trimString(user.user_metadata?.name) ||
    trimString(user.user_metadata?.preferred_username) ||
    trimString(user.email) ||
    user.id
  );
};

export const buildLogtailUserPayload = (user: User): LogtailUserPayload => {
  const payload: LogtailUserPayload = {
    id: user.id,
    username: resolveUsername(user),
  };

  const email = trimString(user.email);
  if (email) {
    payload.email = email;
  }

  const provider = trimString(user.app_metadata?.provider);
  if (provider) {
    payload.provider = provider;
  }

  return payload;
};

export const syncLogtailUser = (user: User | null | undefined) => {
  currentUser = user ? buildLogtailUserPayload(user) : null;
};

const sendWithLevel = async (
  logtail: Logtail,
  level: FrontendLogLevel,
  message: string,
  payload: Record<string, unknown>
) => {
  switch (level) {
    case 'error':
      await logtail.error(message, payload);
      return;
    case 'warn':
      await logtail.warn(message, payload);
      return;
    case 'debug':
      await logtail.debug(message, payload);
      return;
    default:
      await logtail.info(message, payload);
  }
};

export const sendLogtailLog = (
  level: FrontendLogLevel,
  message: string,
  payload: Record<string, unknown> = {}
) => {
  const logtail = getLogtailClient();
  if (!logtail) {
    return;
  }

  const mergedPayload = {
    service: 'frontend',
    gitSha: CLIENT_VERSION_INFO.gitSha,
    env: CLIENT_VERSION_INFO.environment,
    ...payload,
    ...(currentUser ? { user: currentUser } : {}),
  };

  void sendWithLevel(logtail, level, message, mergedPayload).catch(() => undefined);
};

export const flushLogtail = async () => {
  const logtail = getLogtailClient();
  if (!logtail) {
    return;
  }

  await logtail.flush();
};

export const resetLogtailClientForTests = () => {
  browserClient = null;
  browserClientInitialized = false;
  currentUser = null;
};
